import { useState, useMemo } from 'react';
import { 
  Check, 
  X, 
  Trash2, 
  Edit, 
  Save, 
  Search,
  Filter,
  SlidersHorizontal,
  Grid,
  List,
  ChevronDown,
  Star,
  Calendar,
  User,
  CheckSquare,
  XSquare,
  Download
} from 'lucide-react';
import axios from 'axios';
import { showToast } from '../ui/Toast';
import { exportTestimonialsToCSV } from '../../utils/exportUtils';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import './TestimonialsModern.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const TestimonialsModern = ({ testimonials, getAuthHeader, onUpdate }) => {
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState(null);
  
  // New state for advanced features
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'approved', 'pending'
  const [sortBy, setSortBy] = useState('date'); // 'date', 'rating', 'name'
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid'
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filtered and sorted testimonials
  const filteredTestimonials = useMemo(() => {
    let result = [...testimonials];

    // Apply search filter
    if (searchQuery) {
      result = result.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.event_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.comment.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus === 'approved') {
      result = result.filter(t => t.approved);
    } else if (filterStatus === 'pending') {
      result = result.filter(t => !t.approved);
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date);
      } else if (sortBy === 'rating') {
        return (b.rating || 0) - (a.rating || 0);
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    return result;
  }, [testimonials, searchQuery, filterStatus, sortBy]);

  const approveTestimonial = async (id) => {
    try {
      await axios.put(`${API}/testimonials/${id}/approve`, {}, getAuthHeader());
      onUpdate();
      showToast.success('✅ Témoignage approuvé avec succès !');
    } catch (error) {
      showToast.error('❌ Erreur lors de l\'approbation');
    }
  };

  const rejectTestimonial = async (id) => {
    try {
      await axios.put(`${API}/testimonials/${id}/reject`, {}, getAuthHeader());
      onUpdate();
      showToast.warning('⚠️ Témoignage rejeté');
    } catch (error) {
      showToast.error('❌ Erreur lors du rejet');
    }
  };

  const handleDeleteClick = (id) => {
    setTestimonialToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!testimonialToDelete) return;
    try {
      await axios.delete(`${API}/testimonials/${testimonialToDelete}`, getAuthHeader());
      onUpdate();
      showToast.success('🗑️ Témoignage supprimé avec succès');
    } catch (error) {
      showToast.error('❌ Erreur lors de la suppression');
    } finally {
      setShowDeleteConfirm(false);
      setTestimonialToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTestimonialToDelete(null);
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/testimonials/${editingTestimonial.id}`, editingTestimonial, getAuthHeader());
      setEditingTestimonial(null);
      onUpdate();
      showToast.success('✅ Témoignage mis à jour avec succès !');
    } catch (error) {
      showToast.error('❌ Erreur lors de la mise à jour');
    }
  };

  // Bulk operations
  const handleSelectAll = () => {
    if (selectedItems.length === filteredTestimonials.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredTestimonials.map(t => t.id));
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleBulkApprove = async () => {
    const loadingToast = showToast.loading(`Approbation de ${selectedItems.length} témoignages...`);
    try {
      await Promise.all(
        selectedItems.map(id => axios.put(`${API}/testimonials/${id}/approve`, {}, getAuthHeader()))
      );
      onUpdate();
      showToast.success(`✅ ${selectedItems.length} témoignages approuvés !`);
      setSelectedItems([]);
    } catch (error) {
      showToast.error('❌ Erreur lors de l\'approbation en masse');
    }
  };

  const handleBulkReject = async () => {
    const loadingToast = showToast.loading(`Rejet de ${selectedItems.length} témoignages...`);
    try {
      await Promise.all(
        selectedItems.map(id => axios.put(`${API}/testimonials/${id}/reject`, {}, getAuthHeader()))
      );
      onUpdate();
      showToast.warning(`⚠️ ${selectedItems.length} témoignages rejetés`);
      setSelectedItems([]);
    } catch (error) {
      showToast.error('❌ Erreur lors du rejet en masse');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedItems.length} témoignages ?`)) return;
    
    const loadingToast = showToast.loading(`Suppression de ${selectedItems.length} témoignages...`);
    try {
      await Promise.all(
        selectedItems.map(id => axios.delete(`${API}/testimonials/${id}`, getAuthHeader()))
      );
      onUpdate();
      showToast.success(`🗑️ ${selectedItems.length} témoignages supprimés`);
      setSelectedItems([]);
    } catch (error) {
      showToast.error('❌ Erreur lors de la suppression en masse');
    }
  };

  const pendingCount = testimonials.filter(t => !t.approved).length;
  const approvedCount = testimonials.filter(t => t.approved).length;

  return (
    <div className="testimonials-modern">
      {/* Header */}
      <div className="testimonials-header">
        <div>
          <h1 className="testimonials-title">Gestion des Témoignages</h1>
          <p className="testimonials-subtitle">
            {filteredTestimonials.length} témoignages affichés sur {testimonials.length} au total
          </p>
        </div>
        <div className="testimonials-stats-quick">
          <Badge variant="warning" icon={<X size={14} />}>
            {pendingCount} en attente
          </Badge>
          <Badge variant="success" icon={<Check size={14} />}>
            {approvedCount} approuvés
          </Badge>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="testimonials-toolbar">
        <div className="toolbar-row">
          {/* Search */}
          <div className="toolbar-search">
            <Input
              placeholder="Rechercher par nom, type ou commentaire..."
              leftIcon={<Search size={18} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="toolbar-actions">
            <Button
              variant="secondary"
              size="small"
              icon={<Download size={18} />}
              onClick={() => {
                exportTestimonialsToCSV(filteredTestimonials);
                showToast.success('📥 Témoignages exportés en CSV !');
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="secondary"
              size="small"
              icon={<Filter size={18} />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtres
            </Button>
            <Button
              variant="secondary"
              size="small"
              icon={viewMode === 'list' ? <Grid size={18} /> : <List size={18} />}
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            >
              {viewMode === 'list' ? 'Grille' : 'Liste'}
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Statut</label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('all')}
                >
                  Tous ({testimonials.length})
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'approved' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('approved')}
                >
                  Approuvés ({approvedCount})
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('pending')}
                >
                  En attente ({pendingCount})
                </button>
              </div>
            </div>

            <div className="filter-group">
              <label>Trier par</label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${sortBy === 'date' ? 'active' : ''}`}
                  onClick={() => setSortBy('date')}
                >
                  <Calendar size={16} /> Date
                </button>
                <button
                  className={`filter-btn ${sortBy === 'rating' ? 'active' : ''}`}
                  onClick={() => setSortBy('rating')}
                >
                  <Star size={16} /> Note
                </button>
                <button
                  className={`filter-btn ${sortBy === 'name' ? 'active' : ''}`}
                  onClick={() => setSortBy('name')}
                >
                  <User size={16} /> Nom
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Bulk Actions Bar */}
      {selectedItems.length > 0 && (
        <Card className="bulk-actions-bar" variant="primary">
          <div className="bulk-actions-content">
            <div className="bulk-actions-info">
              <CheckSquare size={20} />
              <span>{selectedItems.length} élément(s) sélectionné(s)</span>
            </div>
            <div className="bulk-actions-buttons">
              <Button
                variant="success"
                size="small"
                icon={<Check size={16} />}
                onClick={handleBulkApprove}
              >
                Approuver tout
              </Button>
              <Button
                variant="warning"
                size="small"
                icon={<X size={16} />}
                onClick={handleBulkReject}
              >
                Rejeter tout
              </Button>
              <Button
                variant="danger"
                size="small"
                icon={<Trash2 size={16} />}
                onClick={handleBulkDelete}
              >
                Supprimer tout
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setSelectedItems([])}
              >
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Select All */}
      {filteredTestimonials.length > 0 && (
        <div className="select-all-bar">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={selectedItems.length === filteredTestimonials.length}
              onChange={handleSelectAll}
            />
            <span>Sélectionner tout</span>
          </label>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="custom-modal-overlay" onClick={cancelDelete}>
          <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>⚠️ Confirmer la suppression</h3>
            </div>
            <div className="custom-modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce témoignage ?</p>
              <p className="warning-text">Cette action est irréversible.</p>
            </div>
            <div className="custom-modal-actions">
              <Button variant="secondary" onClick={cancelDelete}>
                Annuler
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Testimonials List/Grid */}
      <div className={`testimonials-${viewMode}`}>
        {filteredTestimonials.length > 0 ? (
          filteredTestimonials.map(testimonial => (
            <Card 
              key={testimonial.id} 
              className={`testimonial-card testimonial-card--${testimonial.approved ? 'approved' : 'pending'} ${selectedItems.includes(testimonial.id) ? 'testimonial-card--selected' : ''}`}
              hover
            >
              {editingTestimonial?.id === testimonial.id ? (
                <div className="testimonial-edit-form">
                  <Input
                    label="Nom"
                    value={editingTestimonial.name}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, name: e.target.value })}
                  />
                  <Input
                    label="Type d'événement"
                    value={editingTestimonial.event_type}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, event_type: e.target.value })}
                  />
                  <Input
                    label="Note (1-5 étoiles)"
                    type="number"
                    min="1"
                    max="5"
                    value={editingTestimonial.rating}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, rating: parseInt(e.target.value) })}
                  />
                  <div className="form-group">
                    <label>Commentaire</label>
                    <textarea
                      value={editingTestimonial.comment}
                      onChange={(e) => setEditingTestimonial({ ...editingTestimonial, comment: e.target.value })}
                      rows="4"
                      className="ui-input"
                    />
                  </div>
                  <div className="testimonial-edit-actions">
                    <Button 
                      variant="primary" 
                      icon={<Save size={16} />}
                      onClick={saveEdit}
                    >
                      Sauvegarder
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={() => setEditingTestimonial(null)}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="testimonial-card-header">
                    <label className="testimonial-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(testimonial.id)}
                        onChange={() => handleSelectItem(testimonial.id)}
                      />
                    </label>
                    <div className="testimonial-info">
                      <h3>{testimonial.name}</h3>
                      <p className="testimonial-meta">
                        {testimonial.event_type} • {testimonial.date}
                      </p>
                    </div>
                    <div className="testimonial-rating">
                      {'⭐'.repeat(testimonial.rating || 5)}
                    </div>
                  </div>
                  <p className="testimonial-comment">{testimonial.comment}</p>
                  <div className="testimonial-footer">
                    <Badge 
                      variant={testimonial.approved ? 'success' : 'warning'}
                      icon={testimonial.approved ? <Check size={14} /> : <X size={14} />}
                    >
                      {testimonial.approved ? 'Approuvé' : 'En attente'}
                    </Badge>
                    <div className="testimonial-actions">
                      {!testimonial.approved && (
                        <Button 
                          variant="success" 
                          size="small"
                          icon={<Check size={16} />}
                          onClick={() => approveTestimonial(testimonial.id)}
                        >
                          Approuver
                        </Button>
                      )}
                      {testimonial.approved && (
                        <Button 
                          variant="warning" 
                          size="small"
                          icon={<X size={16} />}
                          onClick={() => rejectTestimonial(testimonial.id)}
                        >
                          Rejeter
                        </Button>
                      )}
                      <Button 
                        variant="secondary" 
                        size="small"
                        icon={<Edit size={16} />}
                        onClick={() => setEditingTestimonial(testimonial)}
                      >
                        Modifier
                      </Button>
                      <Button 
                        variant="danger" 
                        size="small"
                        icon={<Trash2 size={16} />}
                        onClick={() => handleDeleteClick(testimonial.id)}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          ))
        ) : (
          <Card className="testimonials-empty">
            <p>Aucun témoignage trouvé</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TestimonialsModern;
